/**
 * Open a cursor:// or vscode:// link from the webapp (user gesture required).
 * Pasting the same URL into a browser address bar usually does nothing - OS handlers
 * only run when the link is activated as a navigation from a page or app.
 */
export function launchCursorUri(uri: string): boolean {
  const trimmed = uri.trim();
  if (!trimmed.startsWith('cursor://') && !trimmed.startsWith('vscode://')) {
    return false;
  }

  try {
    const anchor = document.createElement('a');
    anchor.href = trimmed;
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return true;
  } catch {
    try {
      window.location.assign(trimmed);
      return true;
    } catch {
      return false;
    }
  }
}

export function buildOpenWorkspaceUri(params: URLSearchParams): string {
  return `cursor://flownotes.cursor/openWorkspace?${params.toString()}`;
}

/**
 * Materialize-locally-and-reveal URI, handled by the FlowNotes Cursor extension.
 * `folderPath` is the content-relative folder path (e.g. hop-studios/clients/.../todos/123-slug).
 */
export function buildMaterializeUri(folderPath: string): string {
  const params = new URLSearchParams();
  params.set('path', folderPath);
  params.set('reveal', '1');
  return `cursor://flownotes.cursor/materialize?${params.toString()}`;
}

/**
 * Derive a URL-safe project slug from a display project name (e.g. "Greater Good" -> "greater-good").
 * Used as a fallback when no explicit projectSlug is available from the backend.
 */
export function deriveProjectSlug(projectName: string | null | undefined): string | null {
  if (!projectName) return null;
  const slug = projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  return slug || null;
}
